/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import { toast } from '@postscript/components';
import { cloneDeep, isNil, uniqBy } from 'lodash';
import moment from 'moment';

import { formatErrorMessage } from 'utils/errors';
import { MULTIPLIER_FRACTION_DIGITS } from './constants';
import {
  Invoice,
  LedgerMetadata,
  LedgerRecord,
  LedgerRecordTypes,
  MessageRate,
  MessageRateInput,
  MessageTypes,
  RecurringFee,
  UsageCredit,
} from './types';

interface FormattedDollarsOptions {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  displayCurrency?: boolean;
}

export const formatDollars = (
  amount: number,
  {
    minimumFractionDigits = 2,
    maximumFractionDigits,
    displayCurrency = true,
  }: FormattedDollarsOptions = {},
): string => {
  return new Intl.NumberFormat('en-US', {
    style: displayCurrency ? 'currency' : 'decimal',
    minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits ?? minimumFractionDigits,
    currency: 'USD',
  }).format(parseFloat(amount.toString()));
};

const minimumSpendPeriods: Record<number, string> = {
  1: 'Monthly',
  3: 'Quarterly',
  12: 'Annual',
};

export function formatMinimumSpendPeriod(numberOfMonths?: number): string {
  if (!numberOfMonths || numberOfMonths < 0) return 'Period';

  return minimumSpendPeriods[numberOfMonths] ?? `${numberOfMonths}-month`;
}

export function formatMinimumSpendCommitment(
  amountCents: number,
  numberOfMonths = 1,
): string | null {
  if (amountCents <= 0) return null;

  const fractionDigits = amountCents % 100 === 0 ? 0 : 2;

  return `${formatDollars(amountCents / 100, {
    minimumFractionDigits: fractionDigits,
  })} ${formatMinimumSpendPeriod(numberOfMonths).toLowerCase()} minimum spend`;
}

export const formatInputDollars = (
  input: string,
  minimumFractionDigits = 2,
  maximumFractionDigits?: number,
  allowNegativeValues = true,
): string => {
  let a = input;
  a = (a || '0').replace(/,/g, '');
  if (!allowNegativeValues) a = a.replace(/-/g, '');
  if (a.startsWith('-') && a.charAt(1) === '$') a = `-${a.substring(2)}`;
  if (a.startsWith('$')) a = a.substring(1);
  if (Number.isNaN(parseFloat(a))) a = '0';
  a = parseFloat(a).toFixed(maximumFractionDigits ?? minimumFractionDigits);

  return formatDollars(parseFloat(a), {
    minimumFractionDigits,
    maximumFractionDigits,
    displayCurrency: false,
  });
};

export function formatInputMessageRate(rate: string): string {
  return formatInputDollars(rate, 0, MULTIPLIER_FRACTION_DIGITS, false);
}

export function getCents(value: string): number {
  let string = value;

  // Ensure value has at least two decimal places
  if (string.indexOf('.') === -1) {
    string = `${string}.00`;
  }

  // Remove "$" and commas
  string = string.replace(/[^0-9.-]/g, '');

  // Add trailing zeros to at least the one-thousandth place
  string = `${string}000`;

  // Grab indexes to offset decimal point
  const index = string.indexOf('.');
  const offset = 2;
  const offsetIndex = index + offset;

  // Move decimal point to the right by two places (get cents from dollars)
  string = string.replace('.', '');
  string = `${string.slice(0, offsetIndex)}.${string.slice(offsetIndex)}`;

  return parseFloat(string);
}

interface LedgerRecordFilterInput {
  ledgerRecordTypes?: LedgerRecordTypes[];
  descriptions?: string[];
  messageTypes?: MessageTypes[];
}

export function filterLedgerRecords(
  ledgerRecords: LedgerRecord[],
  filters: LedgerRecordFilterInput = {},
): LedgerRecord[] {
  const { ledgerRecordTypes, descriptions, messageTypes } = filters;

  return ledgerRecords
    .filter(({ type }) =>
      ledgerRecordTypes?.length ? ledgerRecordTypes.includes(type) : true,
    )
    .filter(({ description }) =>
      descriptions?.length ? descriptions.includes(description) : true,
    )
    .filter(({ ledgerMetadata }) =>
      messageTypes?.length
        ? messageTypes.includes(ledgerMetadata?.messageType as MessageTypes)
        : true,
    );
}

export function sumLedgerRecords(
  ledgerRecords: LedgerRecord[],
  filters: LedgerRecordFilterInput = {},
): number {
  const { ledgerRecordTypes, descriptions, messageTypes } = filters;

  return filterLedgerRecords(ledgerRecords, {
    ledgerRecordTypes,
    descriptions,
    messageTypes,
  })
    .map(({ amount }) => amount)
    .reduce((a, b) => a + b, 0);
}

export function countLedgerRecords(
  ledgerRecords: LedgerRecord[],
  filters: LedgerRecordFilterInput = {},
): number {
  const { ledgerRecordTypes, descriptions, messageTypes } = filters;

  return filterLedgerRecords(ledgerRecords, {
    ledgerRecordTypes,
    descriptions,
    messageTypes,
  }).length;
}

export function aggregateLedgerRecordsByType(
  ledgerRecords: LedgerRecord[],
  typesToAggregate: LedgerRecordTypes[],
): LedgerRecord[] {
  if (!ledgerRecords.length) return ledgerRecords;

  const typesSet = new Set(typesToAggregate);
  const aggregatedData = new Map<LedgerRecordTypes, LedgerRecord>();
  const nonAggregated: LedgerRecord[] = [];

  ledgerRecords.forEach((record) => {
    const { type, amount } = record;

    if (!typesSet.has(type)) {
      return nonAggregated.push(record);
    }

    const aggregatedRecord = aggregatedData.get(type);

    if (!aggregatedRecord) {
      return aggregatedData.set(type, { ...record });
    }

    aggregatedRecord.amount += amount;
  });

  return [...nonAggregated, ...aggregatedData.values()];
}

export function toggleButtonLoadingState({
  button,
  isLoading,
  originalText,
}: {
  button: HTMLButtonElement | null;
  isLoading: boolean;
  originalText: string;
}): void {
  if (!button) return;

  const mutableButton = button;

  if (isLoading) {
    mutableButton.disabled = true;
    mutableButton.innerText = 'Loading...';
    mutableButton.style.fontWeight = 'var(--body-text-bold-font-weight)';

    return;
  }

  mutableButton.innerText = originalText;
  mutableButton.disabled = false;
}

export function findMessageRateByCodeAndType(
  messageRates: MessageRate[] | undefined,
  countryCode: string,
  messageType: MessageTypes,
): number | undefined {
  const messageRate = messageRates?.find(
    (rate) =>
      rate.countryCode === countryCode && rate.messageType === messageType,
  );

  return messageRate?.messageRate;
}

export function formatMessageRate(amount: number): string {
  return formatDollars(amount / 100, {
    maximumFractionDigits: MULTIPLIER_FRACTION_DIGITS,
  });
}

export function formatEstimatedCost(amount: number): string {
  return formatDollars(amount / 100, { maximumFractionDigits: 2 });
}

export function convertMessageRatesToInputRates(
  messageRates: MessageRate[],
): MessageRateInput[] {
  const inputRates: MessageRateInput[] = [];

  const uniqueRatesByCountry = uniqBy(messageRates, 'countryCode');

  for (const rate of uniqueRatesByCountry) {
    const { countryCode } = rate;

    const getRateByType = (type: MessageTypes): number =>
      messageRates.find(
        (messageRate) =>
          messageRate.countryCode === countryCode &&
          messageRate.messageType === type,
      )?.messageRate ?? 0;

    inputRates.push({
      countryCode,
      formattedSmsRate: formatMessageRate(getRateByType('SMS')),
      formattedMmsRate: formatMessageRate(getRateByType('MMS')),
      formattedRcsBasic: formatMessageRate(getRateByType('RCS_BASIC')),
      formattedRcsSingle: formatMessageRate(getRateByType('RCS_SINGLE')),
    });
  }

  return inputRates;
}

export function convertInputRatesToMessageRates(
  inputRates: MessageRateInput[],
): MessageRate[] {
  const messageRates = inputRates
    .map(
      ({
        countryCode,
        formattedSmsRate,
        formattedMmsRate,
        formattedRcsBasic,
        formattedRcsSingle,
      }) => {
        const rates = [
          {
            countryCode,
            messageRate: getCents(formattedSmsRate),
            messageType: 'SMS' as MessageTypes,
          },
          {
            countryCode,
            messageRate: getCents(formattedRcsBasic),
            messageType: 'RCS_BASIC' as MessageTypes,
          },
          {
            countryCode,
            messageRate: getCents(formattedRcsSingle),
            messageType: 'RCS_SINGLE' as MessageTypes,
          },
        ];

        // Only include MMS rate if it's not 'N/A'
        if (formattedMmsRate !== 'N/A') {
          rates.push({
            countryCode,
            messageRate: getCents(formattedMmsRate),
            messageType: 'MMS' as MessageTypes,
          });
        }

        return rates;
      },
    )
    .flat();
  return messageRates;
}

export function getLedgerRecordsFromInvoices(
  invoices: Invoice[],
): LedgerRecord[] {
  return invoices.map(({ ledgerRecords }) => ledgerRecords).flat() || [];
}

export function findActiveUsageCredits(
  usageCredits: UsageCredit[],
): UsageCredit[] {
  return usageCredits
    .filter(({ isExpired }) => !isExpired)
    .filter(({ amountTotal, amountUsed }) => amountUsed < amountTotal);
}

export function sumUsageCredits(usageCredits: UsageCredit[]): {
  amountTotal: number;
  amountUsed: number;
} {
  return {
    amountTotal: usageCredits.reduce(
      (sum, { amountTotal }) => sum + amountTotal,
      0,
    ),
    amountUsed: usageCredits.reduce(
      (sum, { amountUsed }) => sum + amountUsed,
      0,
    ),
  };
}

export const toastError = (error: unknown) => {
  toast.error(formatErrorMessage(error));
};

function formatRevShareRecordDescription(
  recurringFee: RecurringFee,
  ledgerMetadata: LedgerMetadata,
): string {
  const orderType =
    recurringFee.type === 'SMS_SALES_REVENUE_SHARE_NEW' ? 'new' : 'returning';
  return `${ledgerMetadata.revenueSharePercentage}% of ${formatDollars(
    (ledgerMetadata.totalPretaxPrice ?? 0) / 100,
  )} total ${orderType} order attributed revenue`;
}

export function aggregateRevShareLedgerRecords(
  ledgerRecords: LedgerRecord[],
): LedgerRecord[] {
  const records = new Map<string, LedgerRecord>();

  cloneDeep(ledgerRecords).forEach((record) => {
    const { type, recurringFee, amount, ledgerMetadata } = record;

    /*
     * If the record is not an sms sales revenue share record,
     * doesn't have an associated recurring fee,
     * or the recurring fee type is not new or repeat,
     * or the ledger metadata is missing totalPretaxPrice or revenueSharePercentage,
     * add it to the records map and continue to the next record.
     */
    if (
      type !== 'SMS_SALES_REVENUE_SHARE' ||
      !recurringFee ||
      ![
        'SMS_SALES_REVENUE_SHARE_NEW',
        'SMS_SALES_REVENUE_SHARE_REPEAT',
      ].includes(recurringFee.type) ||
      !ledgerMetadata ||
      isNil(ledgerMetadata?.totalPretaxPrice) ||
      isNil(ledgerMetadata?.revenueSharePercentage)
    ) {
      records.set(record.id.toString(), record);
      return;
    }

    const key = `${recurringFee.type}-${ledgerMetadata.revenueSharePercentage}`;
    const existingRecord = records.get(key);

    if (
      existingRecord?.ledgerMetadata &&
      existingRecord.recurringFee &&
      !isNil(existingRecord?.ledgerMetadata?.totalPretaxPrice)
    ) {
      existingRecord.amount += amount;
      existingRecord.ledgerMetadata.totalPretaxPrice +=
        ledgerMetadata.totalPretaxPrice ?? 0;
      existingRecord.recurringFee.description = formatRevShareRecordDescription(
        recurringFee,
        existingRecord.ledgerMetadata,
      );
      return;
    }

    recurringFee.name = `SMS Sales ${
      recurringFee.type === 'SMS_SALES_REVENUE_SHARE_NEW' ? 'new' : 'repeat'
    } order commission`;
    recurringFee.description = formatRevShareRecordDescription(
      recurringFee,
      ledgerMetadata,
    );
    records.set(key, record);
  });

  return Array.from(records.values());
}

export function aggregateLedgerRecordsByDescription(
  ledgerRecords: LedgerRecord[],
): LedgerRecord[] {
  const records = new Map<string, LedgerRecord>();

  cloneDeep(ledgerRecords).forEach((record) => {
    const { description, amount } = record;
    const key = description;

    const existingRecord = records.get(key);

    if (existingRecord) {
      existingRecord.amount += amount;
      return;
    }

    records.set(key, record);
  });

  return Array.from(records.values());
}

/** Provide the pro-rated amount for a mid-month ProductPlan change */
export function calculateProratedAmount(
  currentPlanAmount: number,
  newPlanAmount: number,
) {
  const today = moment();
  const daysInBillingCycle = today.daysInMonth();
  const endOfMonth = moment().endOf('month');
  const daysRemaining = endOfMonth.diff(today, 'days') + 1; // Charge for today

  const dailyRateDifference =
    (newPlanAmount - currentPlanAmount) / daysInBillingCycle;
  const proratedAmount = dailyRateDifference * daysRemaining;

  return proratedAmount;
}

export function countDecimalPlaces(num: number) {
  const str = num.toString();
  const decimalIndex = str.indexOf('.');
  return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1;
}

export function formatCentsToDollars(
  cents: number,
  numberFormatOptions?: Intl.NumberFormatOptions,
): string {
  const dollars = cents / 100;

  // Whole dollars: no decimals
  if (Number.isInteger(dollars)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  }

  // Calculate how many decimal places we need
  const inputDecimalPlaces = countDecimalPlaces(cents);
  const maxDecimals = Math.max(2, inputDecimalPlaces + 2);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: maxDecimals,
    ...numberFormatOptions,
  }).format(dollars);
}
